// 결재 좌측 사이드바 — 폴더트리: 결재함(카운트) · 결재 양식(카테고리 트리) · 관리
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { useAuth } from "../shared/stores/auth";

type Counts = { waiting: number; drafts: number; in_progress: number; completed: number; rejected: number; referenced: number };
type Form = { id: number; code: string; name: string; category: string };

const INBOX: { id: string; label: string; icon: string; key?: keyof Counts }[] = [
  { id: "waiting", label: "결재 대기", icon: "🖊️", key: "waiting" },
  { id: "in_progress", label: "진행 중", icon: "⏳", key: "in_progress" },
  { id: "drafts", label: "임시 저장", icon: "📝", key: "drafts" },
  { id: "completed", label: "완료", icon: "✅", key: "completed" },
  { id: "rejected", label: "반려", icon: "⛔", key: "rejected" },
  { id: "referenced", label: "수신·참조", icon: "👁️", key: "referenced" },
];

const CAT_LABEL: Record<string, string> = {
  HR: "인사", FINANCE: "재무", GENERAL: "일반", CONTRACT: "계약",
  ATTENDANCE: "근태", SALES: "영업",
};

export function ApprovalSidebar({ onCollapse }: { onCollapse: () => void }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const rawFilter = params.get("filter");
  const composing = params.get("compose");
  const delegating = params.get("delegation");
  const onHome = !rawFilter && !composing && !delegating;

  const isAdmin = useAuth((s) => s.hasRole("SUPER_ADMIN") || s.hasRole("HR_ADMIN") || s.hasRole("FINANCE_ADMIN"));

  const [counts, setCounts] = useState<Counts | null>(null);
  const [forms, setForms] = useState<Form[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem("appr-tree-open") || "") || { inbox: true, forms: true }; }
    catch { return { inbox: true, forms: true }; }
  });

  const loadCounts = useCallback(() => {
    api.get<Counts>("/approval/inbox/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    loadCounts();
    api.get<Form[]>("/approval/forms").then((r) => setForms(r.data)).catch(() => {});
  }, [loadCounts]);
  useEffect(() => { loadCounts(); }, [rawFilter, composing, loadCounts]);
  useEffect(() => {
    const h = () => loadCounts();
    window.addEventListener("approval:refresh", h);
    return () => window.removeEventListener("approval:refresh", h);
  }, [loadCounts]);

  const toggle = (id: string) => setOpen((o) => {
    const next = { ...o, [id]: !o[id] };
    try { localStorage.setItem("appr-tree-open", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // forms grouped by category
  const byCat = forms.reduce<Record<string, Form[]>>((acc, f) => {
    (acc[f.category] ||= []).push(f); return acc;
  }, {});

  const header = (id: string, title: string, action?: React.ReactNode) => (
    <div className="t-subnav-group-title" style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }} onClick={() => toggle(id)}>
      <span style={{ display: "inline-block", marginRight: 5, fontSize: 9, color: "var(--text-3)", transition: "transform .15s", transform: open[id] ? "rotate(90deg)" : "none" }}>▶</span>
      <span style={{ flex: 1 }}>{title}</span>
      {action}
    </div>
  );

  return (
    <>
      <div className="t-subnav-header">
        <div><h3>전자결재</h3><p>결재함 · 양식</p></div>
        <button className="t-subnav-collapse" onClick={onCollapse} title="접기"><I.PanelL size={14} /></button>
      </div>

      <div className="t-subnav-body">
        <div style={{ padding: "4px 8px 8px" }}>
          <button className="t-btn t-btn-primary" style={{ width: "100%", fontSize: 13 }} onClick={() => nav("/approval?compose=1")}>
            <I.Plus size={14} /> 새 결재 작성
          </button>
        </div>

        <div className="t-subnav-group">
          <button className={`t-subnav-item ${onHome ? "active" : ""}`} onClick={() => nav("/approval")}>
            <span style={{ marginRight: 7 }}>🏠</span><span style={{ flex: 1, textAlign: "left" }}>대시보드</span>
          </button>
        </div>

        {/* 결재함 */}
        <div className="t-subnav-group">
          {header("inbox", "결재함")}
          {open.inbox && INBOX.map((it) => {
            const n = it.key && counts ? counts[it.key] : 0;
            const active = !composing && !delegating && rawFilter === it.id;
            return (
              <button key={it.id} className={`t-subnav-item ${active ? "active" : ""}`} onClick={() => nav(`/approval?filter=${it.id}`)}>
                <span style={{ marginRight: 7 }}>{it.icon}</span>
                <span style={{ flex: 1, textAlign: "left" }}>{it.label}</span>
                {n > 0 && <span className="sm-badge" style={{ background: it.id === "waiting" ? "var(--danger,#ef4444)" : "var(--text-3)" }}>{n}</span>}
              </button>
            );
          })}
        </div>

        {/* 결재 양식 (카테고리 트리) */}
        <div className="t-subnav-group">
          {header("forms", "결재 양식")}
          {open.forms && Object.keys(byCat).sort().map((cat) => (
            <div key={cat}>
              <div className="t-subnav-group-title" style={{ display: "flex", alignItems: "center", cursor: "pointer", paddingLeft: 16, textTransform: "none", fontSize: 11.5, color: "var(--text-2)" }} onClick={() => toggle(`cat:${cat}`)}>
                <span style={{ display: "inline-block", marginRight: 4, fontSize: 8, color: "var(--text-3)", transition: "transform .15s", transform: open[`cat:${cat}`] !== false ? "rotate(90deg)" : "none" }}>▶</span>
                📁 {CAT_LABEL[cat] || cat}
              </div>
              {open[`cat:${cat}`] !== false && byCat[cat].map((f) => (
                <button key={f.code} className={`t-subnav-item ${composing === f.code ? "active" : ""}`} style={{ paddingLeft: 34 }} onClick={() => nav(`/approval?compose=${f.code}`)}>
                  <span style={{ marginRight: 6, fontSize: 11 }}>📄</span>
                  <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                </button>
              ))}
            </div>
          ))}
          {open.forms && forms.length === 0 && (
            <div style={{ padding: "6px 16px", fontSize: 11.5, color: "var(--text-3)" }}>등록된 양식이 없습니다</div>
          )}
        </div>

        {/* 개인 설정 */}
        <div className="t-subnav-group">
          <div className="t-subnav-group-title">개인 설정</div>
          <button className={`t-subnav-item ${delegating ? "active" : ""}`} onClick={() => nav("/approval?delegation=1")}>
            <span style={{ marginRight: 7 }}>🔁</span><span style={{ flex: 1, textAlign: "left" }}>위임 · 대결 설정</span>
          </button>
        </div>

        {/* 관리 */}
        {isAdmin && (
          <div className="t-subnav-group">
            <div className="t-subnav-group-title">관리</div>
            <button className="t-subnav-item" onClick={() => nav("/admin")}>
              <span style={{ marginRight: 7 }}>🛠️</span><span style={{ flex: 1, textAlign: "left" }}>양식 관리</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
